import getPCore from './pCoreWindow';
import type { MessagingServiceManager } from './pCoreWindow';

const DEFAULT_MESSAGING_CONTEXT = 'app/primary_1';

interface Subscription {
  unsubscribe: () => void;
}

interface MessageCallback<T> {
  (event: { message: T }): void;
}

function teardownMessagingSubscription(
  messagingServiceManager: MessagingServiceManager,
  subscriptionResult: unknown,
): void {
  if (subscriptionResult == null) {
    return;
  }

  if (
    typeof subscriptionResult === 'object' &&
    'unsubscribe' in subscriptionResult &&
    typeof (subscriptionResult as { unsubscribe?: () => void }).unsubscribe === 'function'
  ) {
    (subscriptionResult as { unsubscribe: () => void }).unsubscribe();
    return;
  }

  messagingServiceManager.unsubscribe(subscriptionResult);
}

export function subscribeToPCoreMessage<T = unknown>(
  matcher: string,
  callback: MessageCallback<T>,
  contextName = DEFAULT_MESSAGING_CONTEXT,
  subscriptionId?: string,
): Subscription | undefined {
  try {
    const pCore = getPCore();
    if (!pCore || typeof pCore.getMessagingServiceManager !== 'function') {
      return undefined;
    }

    const messagingServiceManager = pCore.getMessagingServiceManager();
    if (
      !messagingServiceManager ||
      typeof messagingServiceManager.subscribe !== 'function'
    ) {
      return undefined;
    }

    const wrappedCallback = (event: unknown) => {
      callback(event as { message: T });
    };

    const subscriptionResult = messagingServiceManager.subscribe(
      { matcher, criteria: {} },
      wrappedCallback,
      contextName,
      subscriptionId,
    );

    if (subscriptionResult == null) {
      return undefined;
    }

    return {
      unsubscribe: () => {
        teardownMessagingSubscription(messagingServiceManager, subscriptionResult);
      },
    };
  } catch (error) {
    console.error(`Error subscribing to pCore message "${matcher}":`, error);
    return undefined;
  }
}
